import React, { useEffect, useState } from "react";
import { X, ClockCounterClockwise, ArrowRight, PhoneCall } from "@phosphor-icons/react";
import { teleproApi } from "../../lib/teleproApi";
import { STATUS_BY_ID } from "./callListConstants";

/**
 * Phase 2 — CRM History Modal.
 *
 * Shows the chronological append-only timeline of every interaction this
 * télépro logged on a business. Read-only, never destructive.
 *
 * Props:
 *  - businessId
 *  - businessName (display only)
 *  - open (bool)
 *  - onClose
 */
export default function CrmHistoryModal({ businessId, businessName, open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !businessId) return;
    setLoading(true);
    setError("");
    teleproApi.get(`/me/businesses/${businessId}/history`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => setError(e?.response?.data?.detail || "Chargement impossible"))
      .finally(() => setLoading(false));
  }, [open, businessId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="crm-history-modal-backdrop"
    >
      <div
        className="bg-card border border-border rounded-t-lg sm:rounded-sm shadow-xl w-full sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="crm-history-modal"
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border shrink-0 bg-primary/5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Historique CRM</div>
            <div className="font-display text-base font-medium truncate" data-testid="crm-history-title">
              {businessName}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="text-muted-foreground hover:text-foreground p-1"
            data-testid="crm-history-close"
          >
            <X size={18}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Chargement…</div>
          ) : error ? (
            <div className="text-sm text-secondary bg-secondary/10 border border-secondary/30 rounded-sm p-3">
              {error}
            </div>
          ) : items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="crm-history-empty">
              <ClockCounterClockwise size={28} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60"/>
              <div>Aucun événement enregistré pour l’instant.</div>
              <div className="text-[11px] mt-1">Chaque action que tu poses ici apparaîtra dans cette timeline.</div>
            </div>
          ) : (
            <ol className="relative border-l-2 border-border ml-2 space-y-4" data-testid="crm-history-list">
              {items.map((ev) => (
                <HistoryEvent key={ev.id} ev={ev}/>
              ))}
            </ol>
          )}
        </div>

        <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground shrink-0">
          <b>Historique append-only</b> — chaque action reste tracée pour toujours, jamais écrasée.
        </div>
      </div>
    </div>
  );
}

function HistoryEvent({ ev }) {
  const status = ev.to_status ? STATUS_BY_ID[ev.to_status] : null;
  const from = ev.from_status ? STATUS_BY_ID[ev.from_status] : null;
  const at = ev.at ? new Date(ev.at) : null;
  const dateStr = at ? at.toLocaleString("fr-FR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }) : "";
  return (
    <li className="ml-4" data-testid={`crm-history-event-${ev.id}`}>
      <span className="absolute -left-[9px] flex items-center justify-center w-4 h-4 bg-primary text-primary-foreground rounded-full ring-2 ring-card">
        <PhoneCall size={9} weight="fill"/>
      </span>
      <div className="text-[11px] text-muted-foreground tabular-nums">{dateStr}</div>
      <div className="mt-0.5 text-sm">
        {ev.kind === "call_status" && status ? (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium">Statut :</span>
            {from && (
              <>
                <span className={`inline-flex items-center gap-1 border rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${from.color}`}>
                  {from.label}
                </span>
                <ArrowRight size={10} weight="bold" className="text-muted-foreground"/>
              </>
            )}
            <span className={`inline-flex items-center gap-1 border rounded-sm px-1.5 py-0.5 text-[10px] font-bold uppercase ${status.color}`}>
              {status.label}
            </span>
          </div>
        ) : ev.kind === "contact_info" ? (
          <div className="font-medium">Coordonnées mises à jour</div>
        ) : (
          <div className="font-medium">Note ajoutée</div>
        )}
        {ev.note && (
          <div className="mt-1 text-xs italic text-foreground/80 bg-muted/40 px-2 py-1 rounded-sm">
            « {ev.note} »
          </div>
        )}
        {ev.callback_at && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            Rappel prévu le <b className="text-foreground">{new Date(ev.callback_at).toLocaleString("fr-FR")}</b>
            {ev.callback_reason && <> — <i>{ev.callback_reason}</i></>}
          </div>
        )}
        {(ev.contact_name || ev.contact_phone || ev.contact_email) && (
          <div className="mt-1 text-[11px] text-muted-foreground">
            {ev.contact_name && <span className="mr-2">👤 {ev.contact_name}</span>}
            {ev.contact_phone && <span className="mr-2 font-mono">📞 {ev.contact_phone}</span>}
            {ev.contact_email && <span className="font-mono">✉️ {ev.contact_email}</span>}
          </div>
        )}
        {ev.telepro_code && (
          <div className="mt-1 text-[10px] uppercase tracking-widest text-muted-foreground/80 font-mono">
            {ev.telepro_code}
          </div>
        )}
      </div>
    </li>
  );
}
